// src/utils/notifications.js
/**
 * NOTIFICATIONS (WRITE-SIDE) - Enhanced with Group-Based Inventory Filtering
 * -----------------------------------------------------------------------------
 * Writes notifications to:
 *   ✅ users/{userId}/notifications
 *
 * ✅ NEW: Inventory notifications respect groupIds
 * - Only users whose groupIds intersect with item.groupIds get notified
 * - If item has no groupIds, only admin/owner get notified
 *
 * TTL:
 * - Each notification includes `expireAt` (30 days).
 * - Enable Firestore TTL on collection group `notifications` + field `expireAt`
 *   to auto-delete.
 */

import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  limit,
  doc,
  getDoc,
  Timestamp,
} from "firebase/firestore";
import { auth, db } from "../firebase";

export const NOTIFICATION_TYPES = {
  SAMPLE_REQUEST_CREATED: "SAMPLE_REQUEST_CREATED",
  TERRITORY_ASSIGNED: "TERRITORY_ASSIGNED",
  TERRITORY_CHANGED: "TERRITORY_CHANGED",
  MANAGER_CHANGED: "MANAGER_CHANGED",
  SAMPLE_COMMENT: "SAMPLE_COMMENT",
  LOW_INVENTORY: "LOW_INVENTORY",
  SAMPLE_STATUS_CHANGED: "SAMPLE_STATUS_CHANGED",
  SAMPLE_ORDER_EMAILED: "SAMPLE_ORDER_EMAILED",

  // ✅ always on
  SAMPLE_ASSIGNED: "SAMPLE_ASSIGNED",

  MOST_POPULAR_ITEMS_WEEK: "MOST_POPULAR_ITEMS_WEEK",
  NEW_SAMPLEABLE_ITEM: "NEW_SAMPLEABLE_ITEM",
  FEATURED_ITEM: "FEATURED_ITEM",

  TASK_ASSIGNED_TO_USER: "TASK_ASSIGNED_TO_USER",
  TASK_ASSIGNED_TO_GROUP: "TASK_ASSIGNED_TO_GROUP",
  ACCOUNT_TERRITORY_ASSIGNED: "ACCOUNT_TERRITORY_ASSIGNED",

  // ✅ NEW: Task comment @mention
  TASK_MENTION: "TASK_MENTION",

  // ✅ NEW: Gift seats added to company
  GIFT_SEATS_ADDED: "GIFT_SEATS_ADDED",
};

// -------------------------
// TTL config
// -------------------------
const NOTIFICATION_TTL_DAYS = 30;
const msFromDays = (d) => d * 24 * 60 * 60 * 1000;
const buildExpireAt = () =>
  Timestamp.fromMillis(Date.now() + msFromDays(NOTIFICATION_TTL_DAYS));

// -------------------------
// Small helpers
// -------------------------
const isNonEmptyString = (v) => typeof v === "string" && v.trim().length > 0;

const assertInvariant = (condition, message, details) => {
  if (condition) return;
  const err = new Error(message);
  console.error(err, details || {});
  if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
    throw err;
  }
};

const normalizeRole = (role) => (role || "").toString().trim().toLowerCase();
const isAdminOrOwner = (role) => {
  const r = normalizeRole(role);
  return r === "owner" || r === "admin";
};

const uniq = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

// -------------------------
// Company flag cache
// -------------------------
const _companyFlagsCache = new Map(); // companyId -> { flags, fetchedAt }
const FLAGS_TTL_MS = 60 * 1000;

const getCompanyFlagsCached = async (companyId) => {
  if (!companyId) return {};
  const hit = _companyFlagsCache.get(companyId);
  const now = Date.now();
  if (hit && now - hit.fetchedAt < FLAGS_TTL_MS) return hit.flags || {};

  try {
    const snap = await getDoc(doc(db, "companies", companyId));
    const flags = snap.exists() ? snap.data()?.featureFlags || {} : {};
    _companyFlagsCache.set(companyId, { flags, fetchedAt: now });
    return flags;
  } catch (e) {
    console.warn("[notifications] Could not load company flags (defaulting ON)", {
      companyId,
      error: e,
    });
    return {};
  }
};

const isFlagEnabled = async (companyId, flagKey, defaultValue = true) => {
  if (!flagKey) return defaultValue;
  const flags = await getCompanyFlagsCached(companyId);
  const val = flags?.[flagKey];
  if (typeof val === "boolean") return val;
  return defaultValue;
};

// -------------------------
// Derive myCompanyId (cached)
// -------------------------
const _myCompanyIdCache = { value: null, fetchedAt: 0, uid: null };
const MY_COMPANY_CACHE_MS = 60 * 1000;

const getMyCompanyIdCached = async () => {
  const uid = auth.currentUser?.uid || null;
  if (!uid) return null;

  const now = Date.now();
  if (
    _myCompanyIdCache.uid === uid &&
    _myCompanyIdCache.value &&
    now - _myCompanyIdCache.fetchedAt < MY_COMPANY_CACHE_MS
  ) {
    return _myCompanyIdCache.value;
  }

  try {
    const snap = await getDoc(doc(db, "users", uid));
    const companyId = snap.exists() ? snap.data()?.companyId || null : null;

    _myCompanyIdCache.uid = uid;
    _myCompanyIdCache.value = companyId;
    _myCompanyIdCache.fetchedAt = now;

    return companyId;
  } catch (e) {
    console.error("[notifications] Failed to resolve my companyId from /users", { uid, error: e });
    return null;
  }
};

// -------------------------
// Normalize payload
// -------------------------
const normalizeNotificationData = (data = {}) => {
  const next = { ...(data || {}) };

  if (next.body != null && typeof next.body !== "string") next.body = String(next.body);
  if (next.type != null && typeof next.type !== "string") next.type = String(next.type);
  if (next.title != null && typeof next.title !== "string") next.title = String(next.title);
  if (next.linkPath != null && typeof next.linkPath !== "string") next.linkPath = String(next.linkPath);

  // legacy field guard
  if (next.link != null && next.linkPath == null) {
    delete next.link;
  }

  if (next.dedupeKey != null && typeof next.dedupeKey !== "string") {
    next.dedupeKey = String(next.dedupeKey);
  }

  return next;
};

// -------------------------
// Best-effort warning throttle
// -------------------------
const _warnedPermission = new Set();
const warnOnce = (key, ...args) => {
  if (_warnedPermission.has(key)) return;
  _warnedPermission.add(key);
  console.warn(...args);
};

// -------------------------
// Debug helper: explain permission-denied by checking recipient doc (best-effort)
// -------------------------
const debugRecipientCompany = async ({ recipientUid, actorCompanyId }) => {
  try {
    const snap = await getDoc(doc(db, "users", recipientUid));
    if (!snap.exists()) {
      console.warn("[notifications] Recipient user doc does not exist", { recipientUid, actorCompanyId });
      return;
    }
    const recipientCompanyId = snap.data()?.companyId || null;
    console.warn("[notifications] Recipient companyId debug", {
      recipientUid,
      actorCompanyId,
      recipientCompanyId,
      matches: recipientCompanyId === actorCompanyId,
    });
  } catch (e) {
    console.warn("[notifications] Could not read recipient user doc to debug (permission?)", {
      recipientUid,
      actorCompanyId,
      error: e,
    });
  }
};

// -------------------------
// Core creator (writes to users/{userId}/notifications)
// -------------------------
const createNotificationForUser = async (userIdRaw, data) => {
  const userId = (userIdRaw || "").toString().trim();
  const normalized = normalizeNotificationData(data);

  assertInvariant(isNonEmptyString(userId), "[notifications] BUG: userId is missing/invalid", {
    userIdRaw,
    normalized,
  });

  const effectiveCompanyId =
    isNonEmptyString(normalized.companyId) ? normalized.companyId.trim() : await getMyCompanyIdCached();

  assertInvariant(
    isNonEmptyString(effectiveCompanyId),
    "[notifications] BUG: companyId missing when writing notification",
    {
      userId,
      normalizedCompanyId: normalized.companyId || null,
      derivedCompanyId: effectiveCompanyId,
      actorUid: auth.currentUser?.uid || null,
    }
  );

  // Your invariant: userId should never equal companyId
  assertInvariant(
    userId !== effectiveCompanyId,
    "[notifications] BUG: recipient userId equals companyId (this must never happen)",
    { userId, companyId: effectiveCompanyId, normalized }
  );

  try {
    const ref = collection(db, "users", userId, "notifications");
    const dedupeKey = normalized?.dedupeKey || null;
    const actorUid = auth.currentUser?.uid || null;
    
    // ✅ NEW: Get creator's user data
    let creatorName = null;
    let creatorPhotoURL = null;
    
    if (actorUid) {
      try {
        const creatorDoc = await getDoc(doc(db, "users", actorUid));
        if (creatorDoc.exists()) {
          const creatorData = creatorDoc.data();
          creatorName = creatorData.fullName || creatorData.displayName || auth.currentUser?.displayName || "User";
          creatorPhotoURL = creatorData.photoURL || auth.currentUser?.photoURL || null;
        }
      } catch (err) {
        console.warn("[notifications] Could not fetch creator user data:", err);
      }
    }

    // Dedupe by key for any recipient so repeated status/assignment writes do not double-notify.
    if (dedupeKey) {
      try {
        const dedupeQuery = query(ref, where("dedupeKey", "==", dedupeKey), limit(1));
        const dedupeSnap = await getDocs(dedupeQuery);
        if (!dedupeSnap.empty) {
          console.log("[notifications] Skipping duplicate notification (dedupeKey)", { userId, dedupeKey });
          return;
        }
      } catch (err) {
        console.warn("[notifications] Dedupe check failed (continuing)", { userId, dedupeKey, error: err });
      }
    }

    const expireAt =
      normalized?.expireAt instanceof Timestamp ? normalized.expireAt : buildExpireAt();

    const payload = {
      ...normalized,
      userId,
      companyId: effectiveCompanyId,
      createdByUid: actorUid || null,
      
      // ✅ NEW: Include creator's name and photo
      createdByName: creatorName || "User",
      createdByPhotoURL: creatorPhotoURL,
      
      // Backward compatibility fields
      userName: creatorName || "User",
      userPhotoURL: creatorPhotoURL,

      isRead: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      expireAt,
    };

    console.log("[notifications] Writing notification", {
      path: `users/${userId}/notifications`,
      userId,
      companyId: effectiveCompanyId,
      type: payload.type,
      title: payload.title,
      createdByName: creatorName,
      hasPhoto: !!creatorPhotoURL,
      dedupeKey,
    });

    await addDoc(ref, payload);
  } catch (error) {
    const code = error?.code || error?.name || "unknown";

    if (code === "permission-denied") {
      warnOnce(
        `perm_${userId}_${normalized?.type || "unknown"}`,
        "[notifications] permission-denied writing notification (rules mismatch).",
        {
          userId,
          companyId: effectiveCompanyId,
          type: normalized?.type,
          actorUid: auth.currentUser?.uid || null,
          error,
        }
      );

      // ✅ best-effort: helps you see if recipient companyId mismatch is the real cause
      await debugRecipientCompany({ recipientUid: userId, actorCompanyId: effectiveCompanyId });
    }

    console.warn("[notifications] Failed to write notification (best-effort)", {
      userId,
      companyId: effectiveCompanyId,
      data: normalized,
      error,
    });
  }
};

/**
 * Generic exported helper:
 *   createNotification(userId, { ...data })
 *   createNotification({ userId, ...data })
 */
export const createNotification = async (arg1, arg2) => {
  if (typeof arg1 === "string") return createNotificationForUser(arg1, arg2 || {});
  if (typeof arg1 === "object" && arg1 !== null) {
    const { userId, ...data } = arg1;
    return createNotificationForUser(userId, data);
  }
  console.error("[notifications] BUG: createNotification called with invalid args", { arg1, arg2 });
  if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
    throw new Error("[notifications] Invalid arguments to createNotification");
  }
};

// ============================================================================
// ✅ NEW: GROUP-BASED FILTERING HELPER
// ============================================================================

/**
 * Filter users by group access to an inventory item
 * @param {Array} companyUsers - All users in the company
 * @param {Array} itemGroupIds - The inventory item's groupIds
 * @returns {Array} User IDs who have access
 */
const filterUsersByGroupAccess = (companyUsers = [], itemGroupIds = []) => {
  const itemGroups = Array.isArray(itemGroupIds) ? itemGroupIds : [];

  // If item has no groups, only admin/owner should be notified
  if (itemGroups.length === 0) {
    return companyUsers
      .filter((u) => isAdminOrOwner(u?.role))
      .map((u) => u.id || u.uid)
      .filter(Boolean);
  }

  // Filter users whose groupIds intersect with item's groupIds
  return companyUsers
    .filter((u) => {
      const userGroups = Array.isArray(u?.groupIds) ? u.groupIds : [];
      return itemGroups.some((gid) => userGroups.includes(gid));
    })
    .map((u) => u.id || u.uid)
    .filter(Boolean);
};

// ============================================================================
// High-level notification helpers
// ============================================================================

export const notifyTerritoryAssigned = async ({
  companyId,
  territoryId,
  territoryName,
  assignedUserId,
}) => {
  const enabled = await isFlagEnabled(companyId, "inAppNotifyTerritoryAssigned", true);
  if (!enabled) return;
  if (!assignedUserId) return;

  await createNotificationForUser(assignedUserId, {
    companyId: companyId || null,
    type: NOTIFICATION_TYPES.TERRITORY_ASSIGNED,
    title: "New territory assigned",
    body: `You have been assigned to the territory "${territoryName || "Untitled territory"}".`,
    entityType: "territory",
    entityId: territoryId,
    linkPath: `/territories-map?territoryId=${territoryId}`,
    dedupeKey: `TERRITORY_ASSIGNED_${companyId || "none"}_${territoryId || "noTerritory"}_${assignedUserId}`,
  });
};

export const notifySampleOrderAssigned = async ({
  companyId,
  sampleRequestId,
  sampleRequest,
  assignedUserId,
  assignedByName,
}) => {
  if (!assignedUserId || !sampleRequestId) return;
  const enabled = await isFlagEnabled(companyId, "inAppNotifySampleAssignedToUser", true);
  if (!enabled) return;

  const accountName = sampleRequest?.accountName || "an account";
  const productName =
    sampleRequest?.productName ||
    sampleRequest?.product?.name ||
    sampleRequest?.itemName ||
    "a product";

  await createNotificationForUser(assignedUserId, {
    companyId: companyId || null,
    type: NOTIFICATION_TYPES.SAMPLE_ASSIGNED,
    title: "Sample order assigned to you",
    body: `${assignedByName || "Someone"} assigned you a sample order for ${productName} (${accountName}).`,
    entityType: "sampleRequest",
    entityId: sampleRequestId,
    linkPath: `/sample-orders?requestId=${sampleRequestId}`,
    dedupeKey: `SAMPLE_ASSIGNED_${companyId || "none"}_${sampleRequestId}_${assignedUserId}`,
    meta: { assignedByName: assignedByName || null, accountName, productName },
  });
};

export const notifySampleOrderCreated = async ({
  companyId,
  sampleRequestId,
  sampleRequest,
  companyUsers = [],
  assignedUserId = null,
  creatorUserId = null,
  creatorName = null,
}) => {
  if (!sampleRequestId) return;
  const enabled = await isFlagEnabled(companyId, "inAppNotifySampleRequestCreated", true);
  if (!enabled) return;

  const adminOwnerIds = (companyUsers || [])
    .filter((u) => isAdminOrOwner(u?.role))
    .map((u) => u?.id || u?.uid)
    .filter(Boolean);

  const recipients = uniq([...adminOwnerIds, assignedUserId]).filter(
    (userId) => userId && String(userId) !== String(creatorUserId || "")
  );
  if (!recipients.length) return;

  const accountName = sampleRequest?.accountName || "an account";
  const productName =
    sampleRequest?.productName ||
    sampleRequest?.product?.name ||
    sampleRequest?.itemName ||
    "a product";

  await Promise.allSettled(
    recipients.map((userId) =>
      createNotificationForUser(userId, {
        companyId: companyId || null,
        type: NOTIFICATION_TYPES.SAMPLE_REQUEST_CREATED,
        title: "Sample order created",
        body: `${creatorName || "Someone"} created a sample order for ${productName} (${accountName}).`,
        entityType: "sampleRequest",
        entityId: sampleRequestId,
        linkPath: `/sample-orders?requestId=${sampleRequestId}`,
        dedupeKey: `SAMPLE_REQUEST_CREATED_${companyId || "none"}_${sampleRequestId}_${userId}`,
        meta: { creatorName: creatorName || null, accountName, productName },
      })
    )
  );
};

export const notifySampleStatusChanged = async ({
  companyId,
  sampleRequestId,
  sampleRequest,
  oldStatus,
  newStatus,
  recipientUserIds = [],
  companyUsers = [],
  actorUserId = null,
}) => {
  const enabled = await isFlagEnabled(companyId, "inAppNotifySampleStatusChanged", true);
  if (!enabled) return;

  const notifyCreator = await isFlagEnabled(companyId, "inAppNotifySampleStatusToCreator", true);
  const notifyAssignee = await isFlagEnabled(companyId, "inAppNotifySampleStatusToAssignee", true);
  const notifyAdmins = await isFlagEnabled(companyId, "inAppNotifySampleStatusToAdmins", true);

  const accountName = sampleRequest?.accountName || "an account";
  const productName =
    sampleRequest?.productName ||
    sampleRequest?.product?.name ||
    sampleRequest?.itemName ||
    "a product";
  const actorUid =
    actorUserId ||
    sampleRequest?.statusUpdatedBy ||
    sampleRequest?.updatedBy ||
    auth.currentUser?.uid ||
    null;

  const creatorId = sampleRequest?.createdBy || null;
  const assigneeId = sampleRequest?.assignedToId || sampleRequest?.assignedTo || null;
  const adminIds = (Array.isArray(companyUsers) ? companyUsers : [])
    .filter((user) => isAdminOrOwner(user?.role))
    .map((user) => user?.id || user?.uid)
    .filter(Boolean);

  const recipients = uniq([
    ...((recipientUserIds || []).filter(Boolean)),
    ...(notifyCreator ? [creatorId] : []),
    ...(notifyAssignee ? [assigneeId] : []),
    ...(notifyAdmins ? adminIds : []),
  ]).filter((userId) => userId && userId !== actorUid);

  if (!recipients.length) return;

  await Promise.all(
    recipients.map((userId) =>
      createNotificationForUser(userId, {
        companyId: companyId || null,
        type: NOTIFICATION_TYPES.SAMPLE_STATUS_CHANGED,
        title: "Sample order status updated",
        body: `The sample for ${productName} (${accountName}) moved from ${oldStatus || "previous"} to ${
          newStatus || "new"
        } status.`,
        entityType: "sampleRequest",
        entityId: sampleRequestId,
        linkPath: `/sample-orders?requestId=${sampleRequestId}`,
        dedupeKey: `SAMPLE_STATUS_CHANGED_${companyId || "none"}_${sampleRequestId || "noReq"}_${userId}_${newStatus || "unknown"}`,
      })
    )
  );
};

// ============================================================================
// ✅ SAMPLE ORDER COMMENTS: notify admins/owners + assigned user
// ============================================================================

export const notifySampleCommentAdded = async ({
  companyId,
  sampleRequestId,
  sampleRequest,
  commentId,
  commentText,
  commenterName,
  companyUsers = [],
  assignedToId = null,
  mentionedUserIds = [],
}) => {
  const enabled = await isFlagEnabled(companyId, "inAppNotifySampleComment", true);
  if (!enabled) return;

  if (!companyId || !sampleRequestId || !commentId) return;

  const actorUid = auth.currentUser?.uid || null;

  // Admin/owner recipients
  const adminOwnerIds = (companyUsers || [])
    .filter((u) => isAdminOrOwner(u?.role))
    .map((u) => u?.id || u?.uid)
    .filter(Boolean);

  // Assigned user recipient
  const assignedId = assignedToId || sampleRequest?.assignedToId || sampleRequest?.assignedTo || null;

  const recipientsRaw = uniq([...adminOwnerIds, assignedId, ...(Array.isArray(mentionedUserIds) ? mentionedUserIds : [])]);

  // never notify self, and never notify companyId-as-userId bug
  const recipients = recipientsRaw.filter((id) => id && id !== actorUid && id !== companyId);
  if (!recipients.length) return;

  const accountName = sampleRequest?.accountName || "an account";
  const productName =
    sampleRequest?.productName ||
    sampleRequest?.product?.name ||
    sampleRequest?.itemName ||
    "a product";

  const who = (commenterName || "Someone").toString();
  const preview = (commentText || "")
    .toString()
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 160);

  const title = "New comment on sample order";
  const body = preview
    ? `${who} commented on ${productName} (${accountName}): "${preview}"`
    : `${who} commented on ${productName} (${accountName}).`;

  const results = await Promise.allSettled(
    recipients.map((userId) =>
      createNotificationForUser(userId, {
        companyId: companyId || null,
        type: NOTIFICATION_TYPES.SAMPLE_COMMENT,
        title,
        body,
        entityType: "sampleRequest",
        entityId: sampleRequestId,
        linkPath: `/sample-orders?requestId=${sampleRequestId}&commentId=${commentId}`,
        dedupeKey: `SAMPLE_COMMENT_${companyId || "none"}_${sampleRequestId}_${commentId}_${userId}`,
        meta: {
          commenterName: who,
          commentId,
          accountName,
          productName,
        },
      })
    )
  );

  results.forEach((r, i) => {
    if (r.status === "rejected") {
      console.error("[notifications] notifySampleCommentAdded FAILED", {
        recipientUserId: recipients[i],
        code: r.reason?.code,
        message: r.reason?.message,
        reason: r.reason,
      });
    }
  });
};

// ============================================================================
// ✅ INVENTORY / CATALOG NOTIFIERS (GROUP-BASED)
// ============================================================================

/**
 * ✅ Notify about low inventory - respects groupIds
 * Only users with group access to the item get notified
 */
export const notifyLowInventory = async ({
  companyId,
  inventoryItemId,
  inventoryItem,
  companyUsers = [],
}) => {
  const enabled = await isFlagEnabled(companyId, "notifyLowStock", false);
  if (!enabled) return;

  const itemGroupIds = Array.isArray(inventoryItem?.groupIds) ? inventoryItem.groupIds : [];
  const recipientIds = filterUsersByGroupAccess(companyUsers, itemGroupIds);

  if (recipientIds.length === 0) return;

  const name = inventoryItem?.name || "Inventory item";
  const sku = inventoryItem?.sku ? ` (${inventoryItem.sku})` : "";
  const currentQty = inventoryItem?.stock ?? inventoryItem?.currentQty ?? null;
  const qtyText = typeof currentQty === "number" ? ` Current qty: ${currentQty}.` : "";

  await Promise.all(
    recipientIds.map((userId) =>
      createNotificationForUser(userId, {
        companyId: companyId || null,
        type: NOTIFICATION_TYPES.LOW_INVENTORY,
        title: "Low inventory alert",
        body: `${name}${sku} has hit its low inventory threshold.${qtyText}`,
        entityType: "inventory",
        entityId: inventoryItemId,
        linkPath: `/inventory?itemId=${inventoryItemId}`,
        dedupeKey: `LOW_INVENTORY_${companyId || "none"}_${inventoryItemId || "noItem"}_${userId}`,
      })
    )
  );
};

/**
 * ✅ Notify about new sampleable items - respects groupIds
 * Only users with group access to the item get notified
 */
export const notifyNewSampleableItem = async ({
  companyId,
  inventoryItemId,
  inventoryItem,
  companyUsers = [],
}) => {
  const enabled = await isFlagEnabled(companyId, "notifyNewSampleableItem", false);
  if (!enabled) return;

  const itemGroupIds = Array.isArray(inventoryItem?.groupIds) ? inventoryItem.groupIds : [];
  const recipientIds = filterUsersByGroupAccess(companyUsers, itemGroupIds);

  if (recipientIds.length === 0) return;

  const name = inventoryItem?.name || "New item";

  await Promise.all(
    recipientIds.map((userId) =>
      createNotificationForUser(userId, {
        companyId: companyId || null,
        type: NOTIFICATION_TYPES.NEW_SAMPLEABLE_ITEM,
        title: "New sampleable item available",
        body: `${name} is now available for sampling.`,
        entityType: "inventory",
        entityId: inventoryItemId,
        linkPath: `/inventory?itemId=${inventoryItemId}`,
        dedupeKey: `NEW_SAMPLEABLE_${companyId || "none"}_${inventoryItemId}_${userId}`,
      })
    )
  );
};

/**
 * ✅ Notify about featured items - respects groupIds
 * Only users with group access to the item get notified
 */
export const notifyFeaturedItem = async ({
  companyId,
  inventoryItemId,
  inventoryItem,
  companyUsers = [],
}) => {
  const enabled = await isFlagEnabled(companyId, "notifyFeaturedItem", false);
  if (!enabled) return;

  const itemGroupIds = Array.isArray(inventoryItem?.groupIds) ? inventoryItem.groupIds : [];
  const recipientIds = filterUsersByGroupAccess(companyUsers, itemGroupIds);

  if (recipientIds.length === 0) return;

  const name = inventoryItem?.name || "Featured item";

  await Promise.all(
    recipientIds.map((userId) =>
      createNotificationForUser(userId, {
        companyId: companyId || null,
        type: NOTIFICATION_TYPES.FEATURED_ITEM,
        title: "Featured item",
        body: `${name} is now featured in the catalog.`,
        entityType: "inventory",
        entityId: inventoryItemId,
        linkPath: `/inventory?itemId=${inventoryItemId}`,
        dedupeKey: `FEATURED_ITEM_${companyId || "none"}_${inventoryItemId}_${userId}`,
      })
    )
  );
};

/**
 * ✅ Notify about most popular items this week - respects groupIds
 * Only users with group access to ALL items in the list get the notification
 */
export const notifyMostPopularItemsWeek = async ({
  companyId,
  popularItems = [], // Array of { itemId, itemName, sampleCount, groupIds }
  companyUsers = [],
}) => {
  const enabled = await isFlagEnabled(companyId, "notifyMostPopularItemsThisWeek", false);
  if (!enabled) return;
  if (popularItems.length === 0) return;

  // For each user, check if they have access to at least one popular item
  const userItemMap = new Map(); // userId -> items they can see

  companyUsers.forEach((user) => {
    const userGroups = Array.isArray(user?.groupIds) ? user.groupIds : [];
    const userId = user.id || user.uid;
    if (!userId) return;

    const accessibleItems = popularItems.filter((item) => {
      const itemGroups = Array.isArray(item?.groupIds) ? item.groupIds : [];
      
      // If item has no groups, only admin/owner can see it
      if (itemGroups.length === 0) {
        return isAdminOrOwner(user?.role);
      }

      // Check if user has access
      return itemGroups.some((gid) => userGroups.includes(gid));
    });

    if (accessibleItems.length > 0) {
      userItemMap.set(userId, accessibleItems);
    }
  });

  if (userItemMap.size === 0) return;

  // Send notifications to users who can see at least one item
  const results = await Promise.allSettled(
    Array.from(userItemMap.entries()).map(([userId, items]) => {
      const itemList = items
        .slice(0, 3)
        .map((it) => `${it.itemName} (${it.sampleCount} samples)`)
        .join(", ");

      const moreCount = items.length > 3 ? items.length - 3 : 0;
      const moreText = moreCount > 0 ? ` and ${moreCount} more` : "";

      return createNotificationForUser(userId, {
        companyId: companyId || null,
        type: NOTIFICATION_TYPES.MOST_POPULAR_ITEMS_WEEK,
        title: "Most popular items this week",
        body: `Top sampled items: ${itemList}${moreText}.`,
        entityType: "inventory",
        linkPath: `/inventory`,
        dedupeKey: `POPULAR_ITEMS_WEEK_${companyId || "none"}_${userId}_${new Date().toISOString().slice(0, 10)}`,
        meta: { items: items.slice(0, 10) }, // Store up to 10 items
      });
    })
  );

  results.forEach((r, i) => {
    if (r.status === "rejected") {
      console.error("[notifications] notifyMostPopularItemsWeek FAILED", {
        error: r.reason,
      });
    }
  });
};

// ============================================================================
// TASK NOTIFICATIONS
// ============================================================================

export const notifyTaskReminder = async ({
  companyId,
  taskId,
  taskTitle,
  assignedUserId,
  remindedByName,
  dueDateLabel,
}) => {
  const enabled = await isFlagEnabled(companyId, "inAppNotifyTaskAssignedToUser", true);
  if (!enabled) return;
  if (!assignedUserId || !taskId) return;

  await createNotificationForUser(assignedUserId, {
    companyId: companyId || null,
    type: NOTIFICATION_TYPES.TASK_ASSIGNED_TO_USER,
    title: "Task reminder",
    body: `${remindedByName || "Someone"} asked you to review: ${taskTitle || "a task"}${dueDateLabel ? ` (${dueDateLabel})` : ""}.`,
    entityType: "task",
    entityId: taskId,
    linkPath: `/tasks?taskId=${taskId}`,
    dedupeKey: `TASK_REMINDER_${companyId || "none"}_${taskId}_${assignedUserId}_${new Date().toISOString().slice(0, 10)}`,
  });
};

export const notifyTaskAssignedToUser = async ({
  companyId,
  taskId,
  taskTitle,
  assignedUserId,
  assignedByName,
}) => {
  const enabled = await isFlagEnabled(companyId, "inAppNotifyTaskAssignedToUser", true);
  if (!enabled) return;
  if (!assignedUserId || !taskId) return;

  await createNotificationForUser(assignedUserId, {
    companyId: companyId || null,
    type: NOTIFICATION_TYPES.TASK_ASSIGNED_TO_USER,
    title: "Task assigned to you",
    body: `${assignedByName || "Someone"} assigned you: ${taskTitle || "a task"}.`,
    entityType: "task",
    entityId: taskId,
    linkPath: `/tasks?taskId=${taskId}`,
    dedupeKey: `TASK_ASSIGNED_TO_USER_${companyId || "none"}_${taskId}_${assignedUserId}`,
  });
};

export const notifyTaskAssignedToGroup = async ({
  companyId,
  taskId,
  taskTitle,
  groupId,
  groupName,
  recipientUserIds = [],
}) => {
  const enabled = await isFlagEnabled(companyId, "inAppNotifyTaskAssignedToGroup", true);
  if (!enabled) return;
  if (!taskId) return;

  await Promise.all(
    (recipientUserIds || []).map((userId) =>
      createNotificationForUser(userId, {
        companyId: companyId || null,
        type: NOTIFICATION_TYPES.TASK_ASSIGNED_TO_GROUP,
        title: "Task assigned to your team",
        body: `A task was assigned to ${groupName || "your team"}: ${taskTitle || "a task"}.`,
        entityType: "task",
        entityId: taskId,
        linkPath: `/tasks?taskId=${taskId}`,
        dedupeKey: `TASK_ASSIGNED_TO_GROUP_${companyId || "none"}_${taskId}_${groupId || "noGroup"}_${userId}`,
        meta: { groupId: groupId || null, groupName: groupName || null },
      })
    )
  );
};

export const notifyAccountTerritoryAssigned = async ({
  companyId,
  accountId,
  accountName,
  territoryId,
  territoryName,
  recipientUserIds = [],
}) => {
  const enabled = await isFlagEnabled(companyId, "inAppNotifyTerritoryAssigned", true);
  if (!enabled) return;
  if (!accountId) return;

  await Promise.all(
    (recipientUserIds || []).map((userId) =>
      createNotificationForUser(userId, {
        companyId: companyId || null,
        type: NOTIFICATION_TYPES.ACCOUNT_TERRITORY_ASSIGNED,
        title: "Account territory updated",
        body: `${accountName || "An account"} was assigned to ${territoryName || "a territory"}.`,
        entityType: "account",
        entityId: accountId,
        linkPath: `/accounts?accountId=${accountId}`,
        dedupeKey: `ACCOUNT_TERRITORY_ASSIGNED_${companyId || "none"}_${accountId}_${territoryId || "noTerritory"}_${userId}`,
        meta: { territoryId: territoryId || null, territoryName: territoryName || null },
      })
    )
  );
};

// ============================================================================
// TASK COMMENTS: @MENTION NOTIFIER
// ============================================================================

export const notifyTaskMentionedInComment = async ({
  companyId,
  taskId,
  taskTitle,
  commentId,
  commentText,
  commenterName,
  mentionedUserIds = [],
}) => {
  if (!taskId || !commentId) return;

  const actorUid = auth.currentUser?.uid || null;

  const uniqMentions = Array.from(new Set((mentionedUserIds || []).filter(Boolean)));
  const recipients = actorUid ? uniqMentions.filter((uid) => uid !== actorUid) : uniqMentions;
  if (!recipients.length) return;

  const safeTitle = (taskTitle || "Task").toString();
  const who = (commenterName || "Someone").toString();

  const preview = (commentText || "")
    .toString()
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 140);

  const results = await Promise.allSettled(
    recipients.map((userId) =>
      createNotificationForUser(userId, {
        companyId: companyId || null,
        type: NOTIFICATION_TYPES.TASK_MENTION,
        title: "You were mentioned in a task",
        body: preview
          ? `${who} mentioned you on "${safeTitle}": "${preview}"`
          : `${who} mentioned you on "${safeTitle}".`,
        entityType: "task",
        entityId: taskId,
        linkPath: `/tasks?taskId=${taskId}&commentId=${commentId}`,
        dedupeKey: `TASK_MENTION_${companyId || "none"}_${taskId}_${commentId}_${userId}`,
        meta: {
          taskTitle: safeTitle,
          commenterName: who,
          commentId,
        },
      })
    )
  );

  results.forEach((r, i) => {
    if (r.status === "rejected") {
      console.error("[notifications] notifyTaskMentionedInComment FAILED", {
        mentionedUserId: recipients[i],
        code: r.reason?.code,
        message: r.reason?.message,
        reason: r.reason,
      });
    }
  });
};

// ============================================================================
// ✅ NEW: GIFT SEATS NOTIFICATION
// ============================================================================

/**
 * Notify company owner when gift seats are added
 * @param {string} companyId - The company ID
 * @param {number} adminGift - Number of admin gift seats added
 * @param {number} endUserGift - Number of end-user gift seats added
 * @param {string} addedBy - Name/email of person who added the gift seats
 */
export const notifyGiftSeatsAdded = async ({
  companyId,
  adminGift = 0,
  endUserGift = 0,
  addedBy = "System",
}) => {
  if (!companyId) return;

  try {
    // Find the company owner
    const companyRef = doc(db, "companies", companyId);
    const companySnap = await getDoc(companyRef);

    if (!companySnap.exists()) {
      console.warn("[notifications] Company not found for gift seats notification:", companyId);
      return;
    }

    const companyData = companySnap.data();
    const ownerUid = companyData?.ownerUid;

    if (!ownerUid) {
      console.warn("[notifications] No owner UID found for company:", companyId);
      return;
    }

    // Build the notification message
    const parts = [];
    if (adminGift > 0) {
      parts.push(`${adminGift} admin seat${adminGift !== 1 ? 's' : ''}`);
    }
    if (endUserGift > 0) {
      parts.push(`${endUserGift} end-user seat${endUserGift !== 1 ? 's' : ''}`);
    }

    if (parts.length === 0) {
      console.log("[notifications] No gift seats to notify about");
      return;
    }

    const seatsText = parts.join(" and ");
    const body = `${addedBy} has added ${seatsText} to your account as a gift. These seats are now available for your team!`;

    await createNotificationForUser(ownerUid, {
      companyId,
      type: NOTIFICATION_TYPES.GIFT_SEATS_ADDED,
      title: "🎁 Gift seats added to your account",
      body,
      entityType: "company",
      entityId: companyId,
      linkPath: `/settings/billing`,
      dedupeKey: `GIFT_SEATS_${companyId}_${Date.now()}`, // Include timestamp to allow multiple notifications
      meta: {
        adminGift,
        endUserGift,
        addedBy,
      },
    });

    console.log("[notifications] Gift seats notification sent to owner:", ownerUid);
  } catch (error) {
    console.error("[notifications] Failed to send gift seats notification:", error);
  }
};
